import { Header } from '@/types/Header.ts';
import { useAuthStore } from '@/stores/auth.ts';
import { useStorage } from '@vueuse/core';

const fetchDeviceId = (): string => {
	try {
		const v = useStorage('device-id', '');
		if (v.value === '') {
			v.value = crypto.randomUUID();
			localStorage.setItem('device-id', v.value);
		}

		return v.value;
	} catch {
		// For webviews, LocalStorage is NOT enabled
		return '';
	}
};

const commonHeaders: Header = {
	Accept: 'application/json',
	'Content-Type': 'application/json',
	Cache: 'default',
	Authorization: '',
	'X-Device-ID': fetchDeviceId(),
};

const isLux = window.location.host.includes('up-luxembourg');

// account-api.* (uzzer-backend): the default host for every call.
const commonUrl: string | undefined = isLux ? import.meta.env.VITE_API_LUX_URL : import.meta.env.VITE_API_URL;

// api.* (Fridom): host that owns the user address (read + write). Distinct from the account-api host.
const fridomUrl: string | undefined = isLux ? import.meta.env.VITE_FRIDOM_LUX_URL : import.meta.env.VITE_FRIDOM_URL;

const checkResponse = async (response: Response, includeStatus?: boolean, usedSessionToken: boolean = true) => {
	if (!response.ok) {
		if (response.status === 401) {
			const authStore = useAuthStore();
			if (usedSessionToken && authStore.isLogged) {
				authStore.logout();
				window.location.href = '/';
				throw new Error('Session expired');
			}
			if (includeStatus) {
				throw '401';
			}
		}
		throw new Error(`HTTP error, status = ${response.status}`);
	}
	return response;
};

const get = async (
	url: string,
	params?: any,
	token?: string,
	includeStatus?: boolean,
	baseUrl: string | undefined = commonUrl,
): Promise<Response> => {
	commonHeaders.Authorization = `Bearer ${token || useAuthStore().token}`;
	const parameters = params ? `?${new URLSearchParams(params).toString()}` : '';
	const response = await fetch(`${baseUrl}${url}${parameters}`, {
		method: 'GET',
		headers: commonHeaders,
	});

	return await checkResponse(response, includeStatus, !token);
};

const post = async (
	url: string,
	body: any,
	includeStatus?: boolean,
	token?: string,
	baseUrl: string | undefined = commonUrl,
): Promise<Response> => {
	commonHeaders.Authorization = `Bearer ${token || useAuthStore().token}`;

	try {
		const response = await fetch(`${baseUrl}${url}`, {
			method: 'POST',
			body: JSON.stringify(body),
			headers: commonHeaders,
		});

		return await checkResponse(response, includeStatus, !token);
	} catch (error) {
		console.error(`Error in POST request: ${error}`);
		throw error;
	}
};

const put = async (url: string, body: any, baseUrl: string | undefined = commonUrl): Promise<Response> => {
	commonHeaders.Authorization = `Bearer ${useAuthStore().token}`;

	try {
		const response = await fetch(`${baseUrl}${url}`, {
			method: 'PUT',
			body: JSON.stringify(body),
			headers: commonHeaders,
		});

		return await checkResponse(response);
	} catch (error) {
		console.error(`Error in PUT request: ${error}`);
		throw error;
	}
};

const getFridom = (url: string, params?: any, token?: string, includeStatus?: boolean): Promise<Response> =>
	get(url, params, token, includeStatus, fridomUrl);

const putFridom = (url: string, body: any): Promise<Response> => put(url, body, fridomUrl);

const deleteRequest = async (url: string, body?: any): Promise<Response> => {
	commonHeaders.Authorization = `Bearer ${useAuthStore().token}`;

	try {
		const response = await fetch(`${commonUrl}${url}`, {
			method: 'DELETE',
			body: JSON.stringify(body),
			headers: commonHeaders,
		});

		return await checkResponse(response);
	} catch (error) {
		console.error(`Error in DELETE request: ${error}`);
		throw error;
	}
};

export const Api = {
	commonHeaders,
	commonUrl,
	fridomUrl,
	get,
	post,
	getFridom,
	putFridom,
	delete: deleteRequest,
	put,
};
